import dayjs from 'dayjs'

import { DATE_FORMAT } from 'common/constants/dateFormatConstants'
import type {
  DynamicFieldValue,
  EmrCodeDynamicField,
  EmrCodeFieldType,
  HistoricalData,
  HistoricalDataItem,
  HistoricalDynamicFieldValue,
} from 'features/Procedure/state/procedure-api.type'
import { ProcedureTypeCode } from 'features/Procedure/state/procedure.type'

import { normalizeSourceValue } from '../components/manage-procedure/dynamic-fields.utils'
import type { PreviousTreatment, TreatmentFieldValue } from './previous-treatments.types'

const THOUSANDS_SEPARATED = /^\d{1,3}(,\d{3})+(\.\d+)?$/

const OPTION_FIELD_TYPES: EmrCodeFieldType[] = [
  'multiselect',
  'checkboxgroup',
  'dropdown',
  'radiogroup',
]

export const prettifyFieldName = (name: string): string =>
  name
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const isOptionEntryArray = (value: unknown): value is Array<Record<string, string>> =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every((entry) => entry !== null && typeof entry === 'object' && !Array.isArray(entry))

const optionEntryLabels = (entries: Array<Record<string, string>>): string =>
  entries
    .map((entry) => {
      const [firstKey] = Object.keys(entry)
      return firstKey === undefined ? '' : String(entry[firstKey])
    })
    .filter(Boolean)
    .join(', ')

// Refuses (returns undefined) rather than inserting a silently altered number:
// truncated decimals, rounded precision, or ambiguous separators ("6,5").
export const coerceHistoricalValue = (
  field: EmrCodeDynamicField,
  raw: HistoricalDynamicFieldValue,
): DynamicFieldValue => {
  if (typeof raw !== 'string' && typeof raw !== 'number') {
    // [{key: label}] entries only make sense for option fields
    return OPTION_FIELD_TYPES.includes(field.type) ? normalizeSourceValue(field, raw) : undefined
  }

  const trimmed = String(raw).trim()
  const cleaned = THOUSANDS_SEPARATED.test(trimmed) ? trimmed.replace(/,/g, '') : trimmed
  const normalized = normalizeSourceValue(field, cleaned)

  if (
    (field.type === 'integer' || field.type === 'number') &&
    normalized !== undefined &&
    Number(normalized) !== Number(cleaned)
  ) {
    return undefined
  }
  return normalized
}

export const isAppliedValue = (coerced: DynamicFieldValue, current: DynamicFieldValue): boolean => {
  if (Array.isArray(coerced)) {
    return (
      Array.isArray(current) &&
      current.length === coerced.length &&
      coerced.every((entry, index) => entry === current[index])
    )
  }
  return current != null && String(current) === String(coerced)
}

const toDisplayValue = (value: HistoricalDynamicFieldValue): string | undefined => {
  if (typeof value === 'string' || typeof value === 'number') {
    const text = String(value).trim()
    return text || undefined
  }
  if (isOptionEntryArray(value)) {
    return optionEntryLabels(value) || undefined
  }
  return undefined
}

const toFields = (item: HistoricalDataItem): TreatmentFieldValue[] =>
  Object.entries(item.dynamic_fields ?? {}).flatMap(([name, raw]) => {
    const display = toDisplayValue(raw)
    return display ? [{ name, label: prettifyFieldName(name), value: display, raw }] : []
  })

const formatDate = (encounterDate: string): string => {
  const parsed = dayjs(encounterDate)
  return parsed.isValid() ? parsed.format(DATE_FORMAT.DICTIONARY_DATE_FORMAT) : encounterDate
}

const encounterTimestamp = (encounterDate: string): number => {
  const value = dayjs(encounterDate).valueOf()
  return Number.isNaN(value) ? Number.NEGATIVE_INFINITY : value
}

export const toPreviousTreatments = (
  historical?: HistoricalData,
  excludeEncounterUid?: string | null,
): PreviousTreatment[] => {
  const encounters = (historical?.items ?? [])
    .filter((encounter) => encounter.encounter_uuid !== excludeEncounterUid)
    .sort((a, b) => encounterTimestamp(b.encounter_date) - encounterTimestamp(a.encounter_date))

  return encounters.flatMap((encounter) =>
    (encounter.items ?? [])
      .filter((item) => item.code_type === ProcedureTypeCode.CPT4)
      .map((item) => ({
        // item ids are not guaranteed unique across encounters — scope by encounter
        id: `${encounter.encounter_uuid}:${item.id}`,
        encounterUuid: encounter.encounter_uuid,
        codeId: item.code_id ?? null,
        procedure: item.code_text,
        date: formatDate(encounter.encounter_date),
        fields: toFields(item),
      }))
      .filter((treatment) => treatment.fields.length > 0),
  )
}

// All instances of the code from the most recent encounter containing it —
// one encounter can hold several (e.g. Sofwave on head and hand).
export const findLatestEncounterTreatments = (
  historical: HistoricalData | undefined,
  excludeEncounterUid: string | null | undefined,
  codeId: number | null,
): PreviousTreatment[] => {
  if (codeId == null) {
    return []
  }
  const all = toPreviousTreatments(historical, excludeEncounterUid)
  const latest = all.find((treatment) => treatment.codeId === codeId)
  if (!latest) {
    return []
  }
  return all.filter(
    (treatment) => treatment.encounterUuid === latest.encounterUuid && treatment.codeId === codeId,
  )
}
